import { ConfigurationResponse } from 'Interfaces/apiDtos';
import { MobXProviderContext } from 'mobx-react';
import { useContext } from 'react';
import { RootStoreProps } from 'Stores/RootStore.types';

/**
 * Returns the messaging configuration payload when the config request has
 * resolved, or `null` while it is still loading or failed.
 */
export const useMessagingConfig = (): ConfigurationResponse | null => {
  const { config } = useContext<RootStoreProps>(MobXProviderContext);
  const signedInPersonConfig = config?.signedInPersonConfig;

  if (!signedInPersonConfig) {
    return null;
  }

  if (signedInPersonConfig.state !== 'fulfilled') {
    return null;
  }

  return signedInPersonConfig.value?.data ?? null;
};

/**
 * Returns the maximum allowed upload size in bytes.
 * Returns `null` when the config has not been loaded yet or
 * does not expose a limit.
 */
export const useMaxFileSizeBytes = (): number | null => {
  const config = useMessagingConfig();
  const maxFileSizeBytes = config?.maxFileSizeBytes;

  if (typeof maxFileSizeBytes !== 'number' || maxFileSizeBytes <= 0) {
    return null;
  }

  return maxFileSizeBytes;
};

export const DEFAULT_PINNED_CONVERSATIONS_LIMIT = 10;

/**
 * Returns the maximum number of conversations a user can pin.
 * Falls back to `DEFAULT_PINNED_CONVERSATIONS_LIMIT` when the config has not
 * been loaded yet or does not expose a valid limit.
 */
export const usePinnedConversationsLimit = (): number => {
  const config = useMessagingConfig();
  const pinnedConversationsLimit = config?.pinnedConversationsLimit;

  if (
    typeof pinnedConversationsLimit !== 'number' ||
    !Number.isInteger(pinnedConversationsLimit) ||
    pinnedConversationsLimit <= 0
  ) {
    return DEFAULT_PINNED_CONVERSATIONS_LIMIT;
  }

  return pinnedConversationsLimit;
};
